import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import type { AgentId, Card, CardColumnKey, CardPriority, Me, Message, Tag } from '@shared/types';
import { CARD_COLUMNS, CARD_PRIORITIES } from '@shared/types';
import { columnLabel } from '@shared/board';
import { api, assetUrl } from '../lib/api';
import { Markdown } from './Markdown';
import { Orb } from './Orb';
import { ApprovalPreview, compilePins } from './ApprovalPreview';
import type { Pin } from './ApprovalPreview';
import { useAssetUrl } from '../lib/useAssetUrl';
import {
  AGENT_LABELS,
  AGENT_ORDER,
  PRIORITY_META,
  WORK_TYPE_META,
  cardAttachments,
  createdByLabel,
  fmtWhen,
  isImageAsset,
  messageAuthor,
  previewAsset,
  titleCase,
} from '../lib/boardMeta';

/**
 * CardDrawer v2 — the ClickUp-grade card detail (BOARD V2 ADDENDUM §C).
 *
 * Top: header + (for Human review) Approve / Request changes.
 * Field rows: Assignee (agent orbs + you) · Status (Move ▾) · Due date ·
 * Priority chips · Tags (picker with inline create).
 * Tabs: Description (markdown editor — toolbar + Edit/Preview via the existing
 * Markdown component, images through the existing R2 upload) · Comments
 * (composer on top, @mention autocomplete, 👍/🔥 reactions) · Attachments
 * (grid) · Activity (the auto trail only).
 * Right side: the live preview via ApprovalPreview (Annotate pins → change
 * request, View code, Expand — unchanged) + receipts.
 *
 * Approve rides the EXISTING approvals endpoint via refs.approval_id — the
 * board never grows its own execution path.
 */

const COL_CHIP: Record<CardColumnKey, string> = {
  backlog: 'chip-dim',
  in_progress: 'chip-ice',
  qa: 'chip-mint',
  human_review: 'chip-amber',
  scheduled_live: 'chip-violet',
  done: 'chip-mint',
};

type TabKey = 'description' | 'comments' | 'attachments' | 'activity';

/* ------------------------------------------------------------ small popover */

function usePopover(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open, close]);
  return ref;
}

/* ----------------------------------------------------------------- Move ▾ */

function MoveMenu({ card, busy, onMove }: { card: Card; busy: boolean; onMove: (col: CardColumnKey) => void }) {
  const [open, setOpen] = useState(false);
  const ref = usePopover(open, () => setOpen(false));

  return (
    <div className="mmenu" ref={ref}>
      <button type="button" className="btn btn-line sm" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="menu">
        Move ▾
      </button>
      {open && (
        <div className="mmenu-pop" role="menu">
          {CARD_COLUMNS.map((c) => (
            <button
              key={c.key}
              type="button"
              role="menuitem"
              disabled={busy || c.key === card.column_key}
              onClick={() => {
                setOpen(false);
                onMove(c.key);
              }}
            >
              {c.key === card.column_key ? `Already in ${c.label}` : `Move to ${c.label}`}
            </button>
          ))}
          <p className="mmenu-note">Skipping Quinn or Human review is allowed — it just gets noted on the card.</p>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- assignee picker */

function assigneeLabel(assignee: string | null | undefined, me: Me | null): string {
  if (!assignee) return 'No one yet';
  if ((AGENT_ORDER as readonly string[]).includes(assignee)) return AGENT_LABELS[assignee as AgentId];
  if (me && assignee === me.email) return `${me.name} (you)`;
  return titleCase(assignee.split('@')[0] ?? assignee);
}

function AssigneePicker({
  card,
  me,
  busy,
  onAssign,
}: {
  card: Card;
  me: Me | null;
  busy: boolean;
  onAssign: (assignee: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = usePopover(open, () => setOpen(false));
  const current = card.assignee ?? null;
  const isAgent = current !== null && (AGENT_ORDER as readonly string[]).includes(current);

  return (
    <div className="mmenu" ref={ref}>
      <button
        type="button"
        className={`frow-btn${current ? '' : ' nudge'}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={busy}
      >
        {isAgent && <Orb agent={current as AgentId} size="xs" />}
        {!isAgent && current && <span className="favatar" aria-hidden>{assigneeLabel(current, me)[0]}</span>}
        <span>{current ? assigneeLabel(current, me) : 'Assign someone'}</span>
      </button>
      {open && (
        <div className="mmenu-pop" role="menu">
          {AGENT_ORDER.map((a) => (
            <button
              key={a}
              type="button"
              role="menuitem"
              className="with-orb"
              disabled={busy || current === a}
              onClick={() => {
                setOpen(false);
                onAssign(a);
              }}
            >
              <Orb agent={a} size="xs" />
              {AGENT_LABELS[a]}
            </button>
          ))}
          {me && (
            <button
              type="button"
              role="menuitem"
              className="with-orb"
              disabled={busy || current === me.email}
              onClick={() => {
                setOpen(false);
                onAssign(me.email);
              }}
            >
              <span className="favatar" aria-hidden>{me.name[0] ?? '?'}</span>
              {me.name} (you)
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            disabled={busy || current === null}
            onClick={() => {
              setOpen(false);
              onAssign(null);
            }}
          >
            Leave unassigned
          </button>
          <p className="mmenu-note">Assigning an agent makes the work theirs — they pick it up on their next pass.</p>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- tag picker */

function TagPicker({
  card,
  busy,
  onSetTags,
}: {
  card: Card;
  busy: boolean;
  onSetTags: (tagIds: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = usePopover(open, () => setOpen(false));
  const [all, setAll] = useState<Tag[] | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selected = useMemo(() => new Set((card.tags ?? []).map((t) => t.id)), [card.tags]);

  useEffect(() => {
    if (!open) return;
    api
      .get<Tag[]>('/board/tags')
      .then(setAll)
      .catch(() => setErr("Couldn't load the tag list"));
  }, [open]);

  function toggle(tagId: string) {
    const next = new Set(selected);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    onSetTags([...next]);
  }

  async function create() {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setErr(null);
    try {
      const tag = await api.post<Tag>('/board/tags', { name });
      setAll((prev) => {
        if (!prev) return [tag];
        return prev.some((t) => t.id === tag.id) ? prev : [...prev, tag].sort((a, b) => a.name.localeCompare(b.name));
      });
      if (!selected.has(tag.id)) onSetTags([...selected, tag.id]);
      setNewName('');
    } catch (e) {
      setErr(`Couldn't create the tag${e instanceof Error ? ` — ${e.message}` : ''}`);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="mmenu" ref={ref}>
      <button type="button" className="frow-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="menu" disabled={busy}>
        {(card.tags ?? []).length === 0 ? (
          <span>Add tags</span>
        ) : (
          <span className="frow-tags">
            {(card.tags ?? []).map((t) => (
              <span key={t.id} className="tchip" style={{ ['--tag' as string]: t.color }}>
                {t.name}
              </span>
            ))}
          </span>
        )}
      </button>
      {open && (
        <div className="mmenu-pop tagpop" role="menu">
          {all === null && !err && <p className="mmenu-note">Loading tags…</p>}
          {err && <p className="mmenu-note" style={{ color: 'var(--coral)' }}>{err}</p>}
          {all !== null && all.length === 0 && <p className="mmenu-note">No tags yet — make the first one below.</p>}
          {all?.map((t) => (
            <button key={t.id} type="button" role="menuitemcheckbox" aria-checked={selected.has(t.id)} disabled={busy} onClick={() => toggle(t.id)}>
              <span className="tdot" style={{ background: t.color }} aria-hidden />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
              {typeof t.count === 'number' && t.count > 0 && <span className="tcount">{t.count}</span>}
              {selected.has(t.id) && <span aria-hidden>✓</span>}
            </button>
          ))}
          <div className="tag-create">
            <input
              className="field"
              value={newName}
              placeholder="New tag name…"
              maxLength={40}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void create();
                }
              }}
              aria-label="New tag name"
            />
            <button type="button" className="btn btn-line sm" disabled={creating || !newName.trim()} onClick={() => void create()}>
              Create
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------- description tab */

const DESC_HINT = 'What is this card about? Plans, context, links — anything the team should read first.';

function DescriptionTab({
  card,
  busy,
  onSave,
}: {
  card: Card;
  busy: boolean;
  onSave: (md: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // A fresh card resets the editor.
  useEffect(() => {
    setEditing(false);
    setDraft('');
    setPreview(false);
    setErr(null);
  }, [card.id]);

  function startEditing() {
    setDraft(card.description_md ?? '');
    setEditing(true);
    setPreview(false);
    window.setTimeout(() => taRef.current?.focus(), 0);
  }

  /** Splice `insert` over the current selection; put the caret at caretOffset within it. */
  function splice(insert: string, caretOffset?: number) {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? draft.length;
    const end = ta.selectionEnd ?? start;
    const next = draft.slice(0, start) + insert + draft.slice(end);
    setDraft(next);
    const caret = start + (caretOffset ?? insert.length);
    window.setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    }, 0);
  }

  function selection(): { start: number; end: number; text: string } {
    const ta = taRef.current;
    if (!ta) return { start: draft.length, end: draft.length, text: '' };
    const start = ta.selectionStart ?? draft.length;
    const end = ta.selectionEnd ?? start;
    return { start, end, text: draft.slice(start, end) };
  }

  function wrap(mark: string, placeholder: string) {
    const { text } = selection();
    const body = text || placeholder;
    splice(`${mark}${body}${mark}`);
  }

  /** Prefix every selected line (or the current one) — headings and lists. */
  function prefixLines(prefix: (i: number) => string) {
    const ta = taRef.current;
    if (!ta) return;
    const selStart = ta.selectionStart ?? 0;
    const selEnd = ta.selectionEnd ?? selStart;
    const lineStart = draft.lastIndexOf('\n', selStart - 1) + 1;
    const lineEndRaw = draft.indexOf('\n', selEnd);
    const lineEnd = lineEndRaw === -1 ? draft.length : lineEndRaw;
    const lines = draft.slice(lineStart, lineEnd).split('\n');
    const replaced = lines.map((l, i) => `${prefix(i)}${l}`).join('\n');
    const next = draft.slice(0, lineStart) + replaced + draft.slice(lineEnd);
    setDraft(next);
    window.setTimeout(() => {
      ta.focus();
      const caret = lineStart + replaced.length;
      ta.setSelectionRange(caret, caret);
    }, 0);
  }

  function insertLink() {
    const { text } = selection();
    const label = text || 'link text';
    splice(`[${label}](https://)`, label.length + 3 + 8);
  }

  async function uploadImage(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const contentBase64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('could not read the file'));
        r.readAsDataURL(file);
      });
      const asset = await api.post<{ r2_key: string | null }>('/knowledge/upload', {
        kind: 'image',
        title: `Card image — ${card.title}`.slice(0, 120),
        filename: file.name,
        contentBase64,
      });
      if (!asset.r2_key) throw new Error('upload returned no file key');
      splice(`![${file.name}](${asset.r2_key})\n`);
    } catch (ee) {
      setErr(`Couldn't upload the image${ee instanceof Error ? ` — ${ee.message}` : ''}`);
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      /* onSave surfaces its own error */
    }
  }

  if (!editing) {
    return (
      <div className="desc-view">
        {card.description_md ? (
          <Markdown text={card.description_md} style={{ fontSize: 13.5 }} />
        ) : (
          <p className="small dim">{DESC_HINT}</p>
        )}
        <button type="button" className="btn btn-line sm" style={{ marginTop: 12 }} disabled={busy} onClick={startEditing}>
          {card.description_md ? 'Edit description' : 'Write a description'}
        </button>
      </div>
    );
  }

  return (
    <div className="desc-edit">
      <div className="md-toolbar" role="toolbar" aria-label="Formatting">
        <button type="button" title="Bold" aria-label="Bold" disabled={preview} onClick={() => wrap('**', 'bold text')}>
          <b>B</b>
        </button>
        <button type="button" title="Heading" aria-label="Heading 1" disabled={preview} onClick={() => prefixLines(() => '# ')}>
          H1
        </button>
        <button type="button" title="Subheading" aria-label="Heading 2" disabled={preview} onClick={() => prefixLines(() => '## ')}>
          H2
        </button>
        <button type="button" title="Bulleted list" aria-label="Bulleted list" disabled={preview} onClick={() => prefixLines(() => '- ')}>
          •≡
        </button>
        <button type="button" title="Numbered list" aria-label="Numbered list" disabled={preview} onClick={() => prefixLines((i) => `${i + 1}. `)}>
          1.
        </button>
        <button type="button" title="Link" aria-label="Insert link" disabled={preview} onClick={insertLink}>
          ⧉
        </button>
        <button
          type="button"
          title="Add an image"
          aria-label="Upload an image"
          disabled={preview || uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? '…' : '🖼'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => void uploadImage(e)} />
        <span style={{ flex: 1 }} />
        <div className="toggle sm" role="group" aria-label="Edit or preview">
          <button type="button" className={preview ? '' : 'active'} onClick={() => setPreview(false)}>
            EDIT
          </button>
          <button type="button" className={preview ? 'active' : ''} onClick={() => setPreview(true)}>
            PREVIEW
          </button>
        </div>
      </div>

      {preview ? (
        <div className="desc-preview">
          {draft.trim() ? <Markdown text={draft} style={{ fontSize: 13.5 }} /> : <p className="small dim">Nothing written yet.</p>}
        </div>
      ) : (
        <textarea
          ref={taRef}
          className="desc-ta"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={DESC_HINT}
          aria-label="Description"
        />
      )}
      {err && <p className="small" style={{ color: 'var(--coral)' }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-mint sm" disabled={busy || uploading} onClick={() => void save()}>
          Save description
        </button>
        <button type="button" className="btn btn-ghost sm" disabled={busy} onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ comments tab */

const MENTION_TOKENS: string[] = [...AGENT_ORDER, 'founders'];
const REACTION_EMOJI = ['👍', '🔥'] as const;

function reactionsOf(m: Message): Record<string, string[]> {
  const refs = m.refs as { reactions?: unknown } | null;
  const r = refs && typeof refs === 'object' ? refs.reactions : null;
  if (!r || typeof r !== 'object') return {};
  const out: Record<string, string[]> = {};
  for (const [emoji, who] of Object.entries(r as Record<string, unknown>)) {
    if (Array.isArray(who)) out[emoji] = who.filter((w): w is string => typeof w === 'string');
  }
  return out;
}

interface MentionState {
  /** Char index of the '@' being completed. */
  at: number;
  query: string;
  options: string[];
  highlight: number;
}

function findMention(text: string, caret: number): Omit<MentionState, 'highlight'> | null {
  const before = text.slice(0, caret);
  const m = /(^|[\s(\[])@([a-zA-Z]*)$/.exec(before);
  if (!m) return null;
  const query = m[2].toLowerCase();
  const options = MENTION_TOKENS.filter((t) => t.startsWith(query));
  if (options.length === 0) return null;
  return { at: before.length - m[2].length - 1, query: m[2], options: [...options] };
}

function Composer({
  card,
  busy,
  mode,
  text,
  setText,
  pinsCount,
  taRef,
  onSend,
  onCancelChanges,
}: {
  card: Card;
  busy: boolean;
  mode: 'comment' | 'changes';
  text: string;
  setText: (v: string) => void;
  pinsCount: number;
  taRef: RefObject<HTMLTextAreaElement>;
  onSend: () => void;
  onCancelChanges: () => void;
}) {
  const agentName = AGENT_LABELS[card.agent_id] ?? card.agent_id;
  const [mention, setMention] = useState<MentionState | null>(null);

  function refreshMention() {
    const ta = taRef.current;
    if (!ta) return;
    const found = findMention(ta.value, ta.selectionStart ?? ta.value.length);
    setMention(found ? { ...found, highlight: 0 } : null);
  }

  function pick(token: string) {
    const ta = taRef.current;
    if (!ta || !mention) return;
    const caret = ta.selectionStart ?? text.length;
    const next = `${text.slice(0, mention.at)}@${token} ${text.slice(caret)}`;
    setText(next);
    setMention(null);
    const newCaret = mention.at + token.length + 2;
    window.setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    }, 0);
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (!mention) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setMention((m) => (m ? { ...m, highlight: (m.highlight + delta + m.options.length) % m.options.length } : m));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(mention.options[mention.highlight]);
    } else if (e.key === 'Escape') {
      e.stopPropagation(); // close the popup, not the drawer
      setMention(null);
    }
  }

  return (
    <div className={`composer top${mode === 'changes' ? ' changes' : ''}`}>
      {mode === 'changes' && (
        <div className="t-label" style={{ color: 'var(--amber)' }}>
          Change request — this sends the card back to {agentName}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            window.setTimeout(refreshMention, 0);
          }}
          onKeyDown={onKeyDown}
          onClick={refreshMention}
          onBlur={() => window.setTimeout(() => setMention(null), 150)}
          placeholder={
            mode === 'changes' ? `What should ${agentName} change?` : `Leave a note — @mention an agent to loop them in…`
          }
          aria-label={mode === 'changes' ? 'Change request' : 'Comment'}
        />
        {mention && mode === 'comment' && (
          <div className="mention-pop" role="listbox" aria-label="Mention someone">
            {mention.options.map((t, i) => (
              <button
                key={t}
                type="button"
                role="option"
                aria-selected={i === mention.highlight}
                className={i === mention.highlight ? 'hot' : ''}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(t);
                }}
              >
                {t === 'founders' ? (
                  <span className="favatar" aria-hidden>F</span>
                ) : (
                  <Orb agent={t as AgentId} size="xs" />
                )}
                @{t}
                <span className="small dim" style={{ marginLeft: 'auto' }}>
                  {t === 'founders' ? 'ring the founders' : AGENT_LABELS[t as AgentId]}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {mode === 'changes' ? (
          <>
            <button type="button" className="btn btn-mint sm" disabled={busy || !text.trim()} onClick={onSend}>
              Send change request
            </button>
            <button type="button" className="btn btn-ghost sm" disabled={busy} onClick={onCancelChanges}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-line sm" disabled={busy || !text.trim()} onClick={onSend}>
            Comment
          </button>
        )}
        {pinsCount > 0 && (
          <span className="small">
            {pinsCount} pin{pinsCount === 1 ? '' : 's'} on the preview
          </span>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- attachments tab */

function AttachmentTile({ assetKey }: { assetKey: string }) {
  const isUrl = /^https?:\/\//i.test(assetKey);
  const isImage = isImageAsset(assetKey) || (isUrl && /\.(png|jpe?g|gif|webp|avif)(\?|$)/i.test(assetKey));
  const resolved = useAssetUrl(isUrl || !isImage ? null : assetKey);
  const src = isUrl ? assetKey : resolved;
  const name = assetKey.split('/').pop() ?? assetKey;

  async function openIt() {
    const url = isUrl ? assetKey : await assetUrl(assetKey);
    window.open(url, '_blank', 'noopener');
  }

  return (
    <button type="button" className="attach-tile" onClick={() => void openIt()} title={name} aria-label={`Open attachment: ${name}`}>
      {isImage && src ? (
        <img src={src} alt="" loading="lazy" />
      ) : (
        <span className="attach-ext">{(name.split('.').pop() ?? 'file').toUpperCase()}</span>
      )}
      <span className="attach-name">{name}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ drawer */

export interface CardDrawerProps {
  card: Card;
  me: Me | null;
  onClose: () => void;
  /** The board should refetch (moves, comments, approvals all change it). */
  onChanged: () => void;
}

export function CardDrawer({ card, me, onClose, onChanged }: CardDrawerProps) {
  const meEmail = me?.email ?? null;
  const agentName = AGENT_LABELS[card.agent_id] ?? card.agent_id;
  const wt = WORK_TYPE_META[card.work_type] ?? WORK_TYPE_META.other;
  const asset = previewAsset(card);
  const attachments = useMemo(() => cardAttachments(card), [card]);

  const [tab, setTab] = useState<TabKey>('comments');
  const [thread, setThread] = useState<Message[] | null>(null);
  const [threadErr, setThreadErr] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'comment' | 'changes'>('comment');
  const [pins, setPins] = useState<Pin[]>([]);
  const [busy, setBusy] = useState(false);

  const taRef = useRef<HTMLTextAreaElement>(null);

  const loadThread = useCallback(async () => {
    try {
      const rows = await api.get<Message[]>(`/feed?thread=${encodeURIComponent(card.id)}`);
      setThread(rows); // newest first — the composer sits on top, so this reads naturally
      setThreadErr(null);
    } catch (e) {
      setThreadErr(e instanceof Error ? e.message : String(e));
    }
  }, [card.id]);

  // Fresh card → fresh thread + a clean composer.
  useEffect(() => {
    setThread(null);
    setThreadErr(null);
    setText('');
    setMode('comment');
    setPins([]);
    setTab('comments');
  }, [card.id]);

  useEffect(() => {
    void loadThread();
    const t = setInterval(() => void loadThread(), 12000);
    return () => clearInterval(t);
  }, [loadThread]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function act(run: () => Promise<unknown>, failMsg: string) {
    setBusy(true);
    try {
      await run();
      await loadThread();
      onChanged();
    } catch (e) {
      window.alert(`${failMsg}${e instanceof Error ? ` — ${e.message}` : ''}`);
      throw e instanceof Error ? e : new Error(String(e));
    } finally {
      setBusy(false);
    }
  }
  /** act() for handlers that don't need the failure (it's already shown). */
  function actQuiet(run: () => Promise<unknown>, failMsg: string) {
    void act(run, failMsg).catch(() => {});
  }

  function send() {
    const body = text.trim();
    if (!body) return;
    const wasChanges = mode === 'changes';
    const sentPins = pins;
    actQuiet(async () => {
      if (wasChanges) {
        await api.post(`/board/cards/${card.id}/request-changes`, {
          body_md: body,
          pins: sentPins.length ? sentPins : undefined,
        });
        setPins([]);
        setMode('comment');
      } else {
        await api.post(`/board/cards/${card.id}/comments`, { body_md: body });
      }
      setText('');
    }, wasChanges ? "Couldn't send the change request" : "Couldn't post the comment");
  }

  function approve() {
    const approvalId = card.refs?.approval_id;
    actQuiet(async () => {
      if (typeof approvalId === 'string' && approvalId) {
        await api.post(`/approvals/${approvalId}/approve`);
      } else {
        // No approval attached (founder-made card, or one that skipped the
        // gates) — a plain founder move forward is the honest equivalent.
        await api.patch(`/board/cards/${card.id}`, { column_key: 'scheduled_live' });
      }
    }, "Couldn't approve this");
  }

  const patch = (body: Record<string, unknown>, failMsg: string) =>
    actQuiet(() => api.patch(`/board/cards/${card.id}`, body), failMsg);

  function openChangesComposer() {
    setTab('comments');
    setMode('changes');
    // Pins become the first draft of the brief — editable before sending.
    setText((prev) => (prev.trim() ? prev : compilePins(pins.filter((p) => p.note.trim()))));
    window.setTimeout(() => taRef.current?.focus(), 0);
  }

  function react(messageId: string, emoji: string) {
    actQuiet(() => api.post(`/board/messages/${messageId}/react`, { emoji }), "Couldn't add the reaction");
  }

  const comments = (thread ?? []).filter((m) => m.kind === 'card-comment' || m.kind === 'change-request');
  const activity = (thread ?? []).filter((m) => m.kind === 'card-activity');

  const TABS: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: 'description', label: 'Description' },
    { key: 'comments', label: 'Comments', count: comments.length || undefined },
    { key: 'attachments', label: 'Attachments', count: attachments.length || undefined },
    { key: 'activity', label: 'Activity' },
  ];

  return (
    <>
      <div className="drawer-veil" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={`Card: ${card.title}`}>
        <header className="drawer-head">
          <Orb agent={card.agent_id} size="xs" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="t-label">
              {agentName} · {wt.label}
              {card.archived_at ? ' · archived' : ''}
            </div>
            <h3 className="drawer-title">{card.title}</h3>
          </div>
          <button type="button" className="btn btn-ghost sm" onClick={onClose} aria-label="Close card">
            ✕
          </button>
        </header>

        {card.column_key === 'human_review' && !card.archived_at && (
          <div className="drawer-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={approve}>
              Approve
            </button>
            <button type="button" className="btn btn-line" disabled={busy} onClick={openChangesComposer}>
              Request changes
            </button>
            <span className="small">Approving hands it to the runner — receipts land back on this card.</span>
          </div>
        )}

        {/* field rows (addendum §C) */}
        <div className="frows">
          <div className="frow">
            <span className="frow-label">Assignee</span>
            <AssigneePicker card={card} me={me} busy={busy} onAssign={(a) => patch({ assignee: a }, "Couldn't change the assignee")} />
          </div>
          <div className="frow">
            <span className="frow-label">Status</span>
            <span className={`chip ${COL_CHIP[card.column_key]}`}>
              <i />
              {columnLabel(card.column_key)}
            </span>
            <MoveMenu card={card} busy={busy} onMove={(col) => patch({ column_key: col }, "Couldn't move the card")} />
          </div>
          <div className="frow">
            <span className="frow-label">Due date</span>
            <input
              type="date"
              className="field"
              style={{ width: 'auto' }}
              value={card.due_date ?? ''}
              disabled={busy}
              onChange={(e) => patch({ due_date: e.target.value || null }, "Couldn't change the due date")}
              aria-label="Due date"
            />
          </div>
          <div className="frow">
            <span className="frow-label">Priority</span>
            <div className="prio-row" role="group" aria-label="Priority">
              {CARD_PRIORITIES.map((p: CardPriority) => {
                const meta = PRIORITY_META[p];
                const active = card.priority === p;
                return (
                  <button
                    key={p}
                    type="button"
                    className={`pchip ${meta.cls}${active ? ' active' : ''}`}
                    disabled={busy}
                    aria-pressed={active}
                    title={active ? 'Click again to clear' : `Set priority to ${meta.label}`}
                    onClick={() => patch({ priority: active ? null : p }, "Couldn't set the priority")}
                  >
                    <span className="pglyph" aria-hidden>{meta.glyph}</span>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="frow">
            <span className="frow-label">Tags</span>
            <TagPicker card={card} busy={busy} onSetTags={(ids) => patch({ tag_ids: ids }, "Couldn't change the tags")} />
          </div>
          {card.ice_score != null && (
            <div className="frow">
              <span className="frow-label">Leo's score</span>
              <span className="bchip ice-score" title="How big a win × how easy to run">
                ICE {Number(card.ice_score.toFixed(1))}
              </span>
            </div>
          )}
        </div>

        <div className="drawer-body">
          <section className="drawer-main" aria-label="Card detail">
            <nav className="dtabs" role="tablist" aria-label="Card sections">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  className={tab === t.key ? 'active' : ''}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                  {t.count !== undefined && <span className="dtab-count">{t.count}</span>}
                </button>
              ))}
            </nav>

            <div className="dtab-body">
              {tab === 'description' && (
                <>
                  {card.summary_md && (
                    <Markdown text={card.summary_md} className="dim" style={{ fontSize: 13, marginBottom: 12 }} />
                  )}
                  <DescriptionTab
                    card={card}
                    busy={busy}
                    onSave={(md) => act(() => api.patch(`/board/cards/${card.id}`, { description_md: md }), "Couldn't save the description")}
                  />
                </>
              )}

              {tab === 'comments' && (
                <>
                  <Composer
                    card={card}
                    busy={busy}
                    mode={mode}
                    text={text}
                    setText={setText}
                    pinsCount={pins.length}
                    taRef={taRef}
                    onSend={send}
                    onCancelChanges={() => setMode('comment')}
                  />
                  <div className="thread-scroll">
                    {thread === null && !threadErr && <p className="small">Loading the conversation…</p>}
                    {threadErr && (
                      <p className="small" style={{ color: 'var(--coral)' }}>
                        Couldn't load the conversation — {threadErr}
                      </p>
                    )}
                    {thread !== null && comments.length === 0 && (
                      <p className="small">Nothing said here yet — leave a note and {agentName} will see it.</p>
                    )}
                    {comments.map((m) => {
                      const reactions = reactionsOf(m);
                      return (
                        <div key={m.id} className={`tmsg${m.kind === 'change-request' ? ' changereq' : ''}`}>
                          <div className="thead">
                            <span className="who">{messageAuthor(m, meEmail)}</span>
                            {m.kind === 'change-request' && <span className="creq-tag">change request</span>}
                            <span className="when">{fmtWhen(m.created_at)}</span>
                          </div>
                          <div className="tbody">
                            <Markdown text={m.body_md} mentions />
                          </div>
                          <div className="treacts">
                            {REACTION_EMOJI.map((emoji) => {
                              const who = reactions[emoji] ?? [];
                              const mine = meEmail !== null && who.includes(meEmail);
                              return (
                                <button
                                  key={emoji}
                                  type="button"
                                  className={`react-btn${mine ? ' mine' : ''}${who.length ? ' has' : ''}`}
                                  disabled={busy}
                                  onClick={() => react(m.id, emoji)}
                                  aria-pressed={mine}
                                  aria-label={`React with ${emoji}`}
                                  title={who.length ? who.map((w) => titleCase(w.split('@')[0] ?? w)).join(', ') : `React with ${emoji}`}
                                >
                                  {emoji}
                                  {who.length > 0 && <span>{who.length}</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {tab === 'attachments' && (
                <>
                  {attachments.length === 0 ? (
                    <p className="small dim" style={{ padding: '18px 0' }}>
                      Nothing attached yet — previews and images added to the description show up here.
                    </p>
                  ) : (
                    <div className="attach-grid">
                      {attachments.map((k) => (
                        <AttachmentTile key={k} assetKey={k} />
                      ))}
                    </div>
                  )}
                </>
              )}

              {tab === 'activity' && (
                <div className="thread-scroll">
                  {thread === null && !threadErr && <p className="small">Loading the trail…</p>}
                  {thread !== null && activity.length === 0 && (
                    <p className="small dim">No moves yet — the card's history writes itself here.</p>
                  )}
                  {activity.map((m) => (
                    <div key={m.id} className="tact">
                      <span>{m.body_md}</span>
                      <span className="when">{fmtWhen(m.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="drawer-preview" aria-label="Preview">
            {asset ? (
              <ApprovalPreview assetKey={asset} pins={pins} onPinsChange={setPins} />
            ) : (
              <div className="drawer-noprev">
                No preview yet — {agentName} attaches one when the draft is ready to look at.
              </div>
            )}

            {card.result_md && (
              <div style={{ marginTop: 14 }}>
                <div className="t-label">Receipts</div>
                <Markdown text={card.result_md} style={{ fontSize: 13, marginTop: 6 }} />
              </div>
            )}

            <div className="drawer-meta">
              <div>
                <div className="t-label">Added by</div>
                <div className="small" style={{ marginTop: 8 }}>
                  {createdByLabel(card.created_by)} · {new Date(card.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}
